/**
 * Utility to manage minimum expiry date configuration
 * Defaults to 30 days, configurable via REACT_APP_MIN_EXPIRY_DAYS environment variable
 * Minimum allowed value is 1 day
 */

const DEFAULT_MIN_EXPIRY_DAYS = 30
const ABSOLUTE_MIN_EXPIRY_DAYS = 1

/**
 * Gets the configured minimum number of days for expiry date validation
 * @returns number of minimum days in the future for expiry date
 */
export const getMinExpiryDays = (): number => {
  const configValue = process.env.REACT_APP_MIN_EXPIRY_DAYS
  
  if (!configValue) {
    return DEFAULT_MIN_EXPIRY_DAYS
  }

  const days = parseInt(configValue, 10)

  // Validate that the value is a positive integer
  if (isNaN(days) || days < ABSOLUTE_MIN_EXPIRY_DAYS) {
    return DEFAULT_MIN_EXPIRY_DAYS
  }

  return days
}

/**
 * Calculates the minimum allowed expiry date based on current date
 * @returns Date object representing the earliest valid expiry date
 */
export const getMinExpiryDate = (): Date => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const minDate = new Date(today)
  minDate.setDate(minDate.getDate() + getMinExpiryDays())
  return minDate
}

/**
 * Validates if a given date meets the minimum expiry date requirement
 * @param date - The date to validate
 * @returns true if date is valid (>= minimum expiry date), false otherwise
 */
export const isValidExpiryDate = (date: Date | null | undefined): boolean => {
  if (!date) return true // Allow null/undefined values for optional fields
  return date >= getMinExpiryDate()
}

/**
 * Gets a user-friendly error message for expiry date validation
 * @returns validation error message
 */
export const getExpiryDateErrorMessage = (): string => {
  const days = getMinExpiryDays()
  return `Expiry date must be at least ${days} day${days !== 1 ? 's' : ''} in the future`
}
